import { BillingDetailsData } from '@features/campaigns/details/containers/BillingDetails/BillingDetails.dto.ts';
import { ReviewData, reviewDataDTO } from '@features/campaigns/shared/containers/Review/Review.dto';
import {
  CampaignDetails,
  CampaignFormat,
  CampaignFormats,
  CampaignPricing,
  CampaignStatus,
  CampaignType,
} from '@features/campaigns/shared/types/campaign';
import { CampaignId } from '@shared/types/campaign.ts';
import { UserId } from '@shared/types/users.ts';

export type AvailabilityAndCostData = Partial<Omit<CampaignPricing, 'individualImpressions'>> & {
  budget: number;
  inventory: boolean;
};

export interface DetailsAndStatusData {
  campaignStatus: CampaignStatus;
  campaignType: CampaignType;
  creator: string;
  createdBy: UserId;
}

export interface CampaignDetailsViewData extends ReviewData {
  startDate: string;
  endDate: string;
  sequentialId: number;
  billingSequentialId: number | null;
  type: CampaignType;
  formatType: CampaignFormat[];
  availabilityAndCost?: AvailabilityAndCostData;
  detailsAndStatus: DetailsAndStatusData;
  billingDetails: BillingDetailsData;
  campaignId: CampaignId;
}

const getFormatType = (data: CampaignDetails): CampaignFormat[] => {
  const formatType: CampaignFormat[] = [];

  if (data.adProductDisplay !== null) {
    formatType.push(CampaignFormats.DISPLAY);
  }

  if (data.adProductVideo !== null) {
    formatType.push(CampaignFormats.VIDEO);
  }

  return formatType;
};

export function campaignDetailsDTO(data: CampaignDetails): CampaignDetailsViewData {
  return {
    ...reviewDataDTO(data),
    startDate: data.startDate,
    endDate: data.endDate,
    sequentialId: data.sequentialId,
    billingSequentialId: data.billingSequentialId,
    type: data.type,
    formatType: getFormatType(data),
    campaignId: data.id,
    availabilityAndCost: {
      impressions: data.pricing?.impressions,
      bonusImpressions: data.pricing?.bonusImpressions,
      bonusPrice: data.pricing?.bonusPrice,
      availableImpressions: data.pricing?.availableImpressions,
      estimatedReach: data.pricing?.estimatedReach,
      estimatedFrequency: data.pricing?.estimatedFrequency,
      estimatedReachRange: data.pricing?.estimatedReachRange,
      estimatedFrequencyRange: data.pricing?.estimatedFrequencyRange,
      cpm: data.pricing?.cpm,
      targetSize: data.pricing?.targetSize,
      budget: data.budget,
      inventory: data.inventory,
    },
    detailsAndStatus: {
      campaignStatus: data.status,
      campaignType: data.type,
      creator: data.creatorName,
      createdBy: data.createdBy,
    },
    billingDetails: {
      agency: data.agency,
      purchaseOrder: data.purchaseOrder,
      billingContact: data.billingContact,
      authorizedBudget: data.authorizedBudget,
      realBudget: data.realBudget,
      createdBy: data.createdBy,
      advertiserId: data.advertiser?.id || '',
      validFromDate: data.startDate,
      validToDate: data.endDate,
    },
  };
}
